import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@waymail/ui';
import { api } from '../lib/api';
import { useUi } from '../store/ui';

/**
 * Aviso persistente de envio que nao saiu (ADR 0057). Uma mensagem que errou
 * mais de uma vez na fila nao pode ficar so numa coluna do banco: a pessoa
 * acha que enviou. A API entrega a causa classificada; a frase e daqui, para
 * nunca mostrar o texto bruto do servidor nem o que ele possa ter citado.
 */

type Causa =
  | 'sem_resposta'
  | 'conexao_recusada'
  | 'servidor_nao_encontrado'
  | 'tempo_esgotado'
  | 'autenticacao'
  | 'certificado'
  | 'recusada'
  | 'outra';

interface EnvioComProblema {
  id: string;
  accountId: string;
  para: string[];
  tentativas: number;
  estado: 'tentando' | 'falhou';
  causa: Causa;
}

const MOTIVO: Record<Causa, string> = {
  sem_resposta:
    'o servidor de envio não respondeu. Confira a porta e a criptografia (465 usa SSL, 587 usa STARTTLS)',
  conexao_recusada: 'o servidor de envio recusou a conexão',
  servidor_nao_encontrado: 'não foi possível encontrar o servidor de envio',
  tempo_esgotado: 'o servidor de envio demorou demais para responder',
  autenticacao: 'o servidor de envio recusou o usuário ou a senha',
  certificado: 'o certificado do servidor de envio não é confiável',
  recusada: 'o servidor recusou a mensagem',
  outra: 'houve um erro no envio',
};

const CHAVE = ['envios-com-problema'] as const;

export function AvisoDeEnvioPresa() {
  const queryClient = useQueryClient();
  const abrirConfiguracoes = useUi((s) => s.abrirConfiguracoes);

  const envios = useQuery({
    queryKey: CHAVE,
    queryFn: () => api<EnvioComProblema[]>('/outbound/problems'),
    refetchInterval: 30_000,
    // Sem rede o aviso some em vez de piscar erro: o que importa e o ultimo estado bom.
    retry: false,
  });

  const cancelar = useMutation({
    mutationFn: (id: string) => api(`/outbound/${id}`, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CHAVE }),
  });

  const lista = envios.data ?? [];
  if (lista.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-40 flex w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2">
      {lista.slice(0, 3).map((envio) => (
        <Alert key={envio.id} tone="error" className="shadow-lg">
          <p>
            A mensagem para {envio.para.join(', ')} não saiu: {MOTIVO[envio.causa]}.{' '}
            {envio.estado === 'tentando' ? 'Tentando de novo.' : 'O envio foi interrompido.'}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="danger"
              loading={cancelar.isPending && cancelar.variables === envio.id}
              onClick={() => cancelar.mutate(envio.id)}
            >
              Cancelar o envio
            </Button>
            <Button size="sm" variant="ghost" onClick={() => abrirConfiguracoes()}>
              Editar a conta
            </Button>
          </div>
        </Alert>
      ))}
      {lista.length > 3 && (
        <Alert tone="warning">Mais {lista.length - 3} mensagens com envio parado.</Alert>
      )}
    </div>
  );
}
